import React, { useEffect, useState } from 'react'
import { SubmitHandler, SubmitErrorHandler } from 'react-hook-form'
import ControlledAutocompleteCarrier from 'transfix-form/components/ControlledAutocompleteCarrier'
import ControlledAutocompleteShipper from 'transfix-form/components/ControlledAutocompleteShipper'
import ControlledSingleSelect from 'transfix-form/components/ControlledSingleSelect'
import Grid from 'transfix-ui/core/Grid'
import makeStyles from 'transfix-ui/core/styles/makeStyles'
import { IPartnerApplicationTypes } from 'types/graphqlTypes'
import { TSearchSchema } from './SearchSchema'
import { useSearchForm } from './usesearchForm'

const PARTNER_TYPE_OPTIONS = [
  {
    label: 'Shipper',
    value: IPartnerApplicationTypes.ShipperApplication,
  },
  {
    label: 'Carrier',
    value: IPartnerApplicationTypes.CarrierApplication,
  },
]

const useSearchFormStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
  },
  searchField: {
    width: '100%',
  },
}))

export interface ISearchFormProps {
  /**
   * @example (data) => {}
   */
  onSubmitSuccess: SubmitHandler<TSearchSchema>
  /**
   * @example (errors) => {}
   */
  onSubmitError?: SubmitErrorHandler<TSearchSchema>
}

export const SearchForm = ({ onSubmitSuccess, onSubmitError = () => {} }: ISearchFormProps) => {
  const { control, handleSubmit, watch } = useSearchForm({})
  const [partnerApplicationType, setPartnerApplicationType] = useState<IPartnerApplicationTypes>(
    IPartnerApplicationTypes.ShipperApplication
  )

  const classes = useSearchFormStyles()
  const { partnerType } = watch(['partnerType'])

  /**
   * Checks when the partnerType changes and sets the partnerApplicationType
   */
  useEffect(() => {
    setPartnerApplicationType(partnerType)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerType])

  const submitHandler = handleSubmit(onSubmitSuccess, onSubmitError)

  return (
    <form onSubmit={submitHandler} data-test='SearchForm' className={classes.root}>
      <Grid container direction='row' spacing={2} alignItems='center'>
        <Grid item md='auto'>
          <ControlledSingleSelect
            control={control}
            name='partnerType'
            defaultValue={partnerApplicationType}
            SingleSelectProps={{
              id: 'partnerType',
              dataTest: 'partner-type-single-select',
              label: 'Partner Type',
              options: PARTNER_TYPE_OPTIONS,
              clearable: false,
            }}
          />
        </Grid>
        <Grid item md={6}>
          {partnerApplicationType === IPartnerApplicationTypes.ShipperApplication ? (
            <ControlledAutocompleteShipper
              control={control}
              name='company'
              AutocompleteShipperProps={{
                inputIcon: 'Search',
                renderInputType: 'withIcon',
                placeholder: 'Search shipper name',
                dataTest: 'search-input',
                size: 'small',
                onChange: newVal => onSubmitSuccess({ partnerType, company: newVal }),
              }}
            />
          ) : (
            <ControlledAutocompleteCarrier
              control={control}
              name='company'
              onChange={newVal => onSubmitSuccess({ partnerType, company: newVal })}
              AutocompleteCarrierProps={{
                inputIcon: 'Search',
                renderInputType: 'withIcon',
                placeholder: 'Search carrier name',
                dataTest: 'search-input',
                size: 'small',
              }}
            />
          )}
        </Grid>
      </Grid>
    </form>
  )
}
